import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isAxiosError } from 'axios';
import { ConfigProvider, App as AntdApp } from 'antd';
import koKR from 'antd/locale/ko_KR';
import ErrorBoundary from './components/common/ErrorBoundary';
import store from './store';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/common/useAuth';
import toastMsg, { setNotificationInstance } from './lib/toastMsg';
import { appRoutes } from './routes/routeConfig';
import { LOGIN_URL_PATH } from './constants/service/common/urlConstants';
import * as Pages from './pages';
import App from './containers/app/App';

import GlobalError from './components/common/GlobalError';
import { ErrorResponseType, extractErrorMessage } from './lib/utils/checkErrorResponse';
import { ThemeProvider } from './styles/styledComponents/themeComponents';
import theme from './styles/styledComponents/theme';

const NotificationInitializer = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const { notification } = AntdApp.useApp();

  useEffect(() => {
    setNotificationInstance(notification);
  }, [notification]);

  return <>{children}</>;
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isAxiosError(error)) {
        toastMsg.error(extractErrorMessage(error as ErrorResponseType, query?.meta?.errorMessage as string));
      }
    },
    onSuccess: (success, query) => {
      if (query?.meta?.onSuccess && typeof query?.meta?.onSuccess === 'function') {
        query.meta.onSuccess(success);
      } else if (query?.meta?.successMessage) {
        toastMsg.success(query.meta.successMessage as string);
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
      suspense: false,
    },
    mutations: {
      useErrorBoundary: false,
      retry: 0,
    },
  },
});

const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isLoggedIn } = useAuth();

  if (!isLoggedIn) {
    return <Navigate to={LOGIN_URL_PATH} replace />;
  }

  return <>{children}</>;
};

const LoginGuard = ({ children }: { children: React.ReactNode }) => {
  const { isLoggedIn } = useAuth();

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const router = createBrowserRouter(
  [
    {
      path: LOGIN_URL_PATH,
      element: (
        <LoginGuard>
          <Pages.Login />
        </LoginGuard>
      ),
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <App />
        </AuthGuard>
      ),
      children: appRoutes,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

const Root = (): JSX.Element => {
  return (
    <ConfigProvider
      locale={koKR}
      theme={{
        token: {
          colorPrimary: '#1890ff',
        },
        hashed: false,
      }}
    >
      <AntdApp>
        <NotificationInitializer>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <Provider store={store}>
                <AuthProvider>
                  <ErrorBoundary fallback={({ error }) => <GlobalError error={error} />}>
                    <RouterProvider router={router} />
                  </ErrorBoundary>
                </AuthProvider>
              </Provider>
              <ReactQueryDevtools />
            </QueryClientProvider>
          </ThemeProvider>
        </NotificationInitializer>
      </AntdApp>
    </ConfigProvider>
  );
};

export default Root;
